import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Play, RotateCcw, Heart } from 'lucide-react';

export default function KozmikOda() {
  // --- React State (Arayüz ve Skorlar için) ---
  const [gameState, setGameState] = useState('MENU'); // MENU, PLAYING, GAMEOVER
  const [score, setScore] = useState(0);
  const [highScore, setHighScore] = useState(0);
  const [health, setHealth] = useState(100);
  const [level, setLevel] = useState(1);
  const [notification, setNotification] = useState('');
  const [showDamageOverlay, setShowDamageOverlay] = useState(false);

  // --- Game Engine Refs ---
  const canvasRef = useRef(null);
  const requestRef = useRef(null);
  const audioCtxRef = useRef(null);
  
  // Oyun Verileri
  const gameData = useRef({
    width: 0,
    height: 0,
    centerX: 0,
    centerY: 0,
    frames: 0,
    score: 0,
    activeOrbitCount: 2,
    orbitRadii: [],
    obstacleSize: 20,
    speedMultiplier: 1,
    maxOrbits: 5,
    baseSpeed: 0.013,
    
    player: {
      angle: 0,
      orbitIndex: 0,
      currentRadius: 0,
      targetRadius: 0,
      color: '#818cf8',
      trail: [],
      health: 100,
      maxHealth: 100
    },
    
    obstacles: [],
    particles: [],
    stars: []
  });

  // --- Ses Sistemi ---
  const initAudio = () => {
    if (!audioCtxRef.current) {
      audioCtxRef.current = new (window.AudioContext || window.webkitAudioContext)();
    }
    if (audioCtxRef.current.state === 'suspended') {
      audioCtxRef.current.resume();
    }
  };

  const playSound = useCallback((type) => {
    if (!audioCtxRef.current) return;
    
    const ctx = audioCtxRef.current;
    const osc = ctx.createOscillator();
    const gainNode = ctx.createGain();
    
    osc.connect(gainNode);
    gainNode.connect(ctx.destination);
    
    const now = ctx.currentTime;

    if (type === 'move') {
        osc.type = 'triangle';
        osc.frequency.setValueAtTime(300, now);
        osc.frequency.exponentialRampToValueAtTime(600, now + 0.1);
        gainNode.gain.setValueAtTime(0.2, now);
        gainNode.gain.exponentialRampToValueAtTime(0.01, now + 0.1);
        osc.start(now);
        osc.stop(now + 0.1);
    } else if (type === 'hit') {
        osc.type = 'sawtooth';
        osc.frequency.setValueAtTime(150, now);
        osc.frequency.exponentialRampToValueAtTime(50, now + 0.2);
        gainNode.gain.setValueAtTime(0.3, now);
        gainNode.gain.exponentialRampToValueAtTime(0.01, now + 0.2);
        osc.start(now);
        osc.stop(now + 0.2);
    } else if (type === 'heal') {
        osc.type = 'sine';
        osc.frequency.setValueAtTime(800, now);
        osc.frequency.linearRampToValueAtTime(1200, now + 0.15);
        gainNode.gain.setValueAtTime(0.1, now);
        gainNode.gain.exponentialRampToValueAtTime(0.01, now + 0.2);
        osc.start(now);
        osc.stop(now + 0.2);
    } else if (type === 'die') {
        osc.type = 'sawtooth';
        osc.frequency.setValueAtTime(100, now);
        osc.frequency.exponentialRampToValueAtTime(10, now + 0.8);
        gainNode.gain.setValueAtTime(0.5, now);
        gainNode.gain.exponentialRampToValueAtTime(0.01, now + 0.8);
        osc.start(now);
        osc.stop(now + 0.8);
    } else if (type === 'score') {
        osc.type = 'sine';
        osc.frequency.setValueAtTime(600, now); 
        osc.frequency.setValueAtTime(900, now + 0.05);
        gainNode.gain.setValueAtTime(0.05, now);
        gainNode.gain.exponentialRampToValueAtTime(0.01, now + 0.1);
        osc.start(now);
        osc.stop(now + 0.1);
    } else if (type === 'levelup') {
        osc.type = 'square';
        osc.frequency.setValueAtTime(400, now);
        osc.frequency.linearRampToValueAtTime(1200, now + 0.2);
        gainNode.gain.setValueAtTime(0.1, now);
        gainNode.gain.linearRampToValueAtTime(0, now + 0.4);
        osc.start(now);
        osc.stop(now + 0.4);
    }
  }, []);

  // --- Efektler ve Bildirimler ---
  const triggerDamageEffect = useCallback(() => {
    setShowDamageOverlay(true);
    setTimeout(() => setShowDamageOverlay(false), 200);
  }, []);

  const showNotificationMsg = useCallback((text) => {
    setNotification(text);
    setTimeout(() => setNotification(''), 2000);
  }, []);

  // --- Oyun Mantığı ---
  const calculateOrbitRadii = useCallback(() => {
    const gd = gameData.current;
    gd.orbitRadii = [];
    const minDim = Math.min(gd.width, gd.height);
    gd.obstacleSize = minDim * 0.035;
    const startRadius = minDim * 0.15;
    const maxRadius = minDim * 0.42;
    const spacing = (maxRadius - startRadius) / (gd.maxOrbits - 1);
    
    for(let i = 0; i < gd.maxOrbits; i++) {
      gd.orbitRadii.push(maxRadius - (i * spacing));
    }
  }, []);

  const createParticle = useCallback((x, y, color, speedScale = 5) => {
    gameData.current.particles.push({
        x: x,
        y: y,
        vx: (Math.random() - 0.5) * speedScale,
        vy: (Math.random() - 0.5) * speedScale,
        life: 1.0,
        color: color
    });
  }, []);

  const spawnJumpEffect = useCallback(() => {
    const gd = gameData.current;
    const x = gd.centerX + Math.cos(gd.player.angle) * gd.player.currentRadius;
    const y = gd.centerY + Math.sin(gd.player.angle) * gd.player.currentRadius;
    for(let i=0; i<6; i++) createParticle(x, y, '#818cf8', 3);
  }, [createParticle]);

  const spawnObstacle = () => {
    const gd = gameData.current;
    const targetOrbit = Math.floor(Math.random() * gd.activeOrbitCount);
    const spawnAngle = gd.player.angle + Math.PI + (Math.random() * Math.PI * 0.5); 
    
    for (let obs of gd.obstacles) {
        if (Math.abs(obs.angle - spawnAngle) < 0.8) return; 
    }

    const isHeal = Math.random() < 0.15;
    const isUnstable = Math.random() > 0.6; 
    let decaySpeed;
    if (isUnstable) decaySpeed = 0.003 + (Math.random() * 0.002); 
    else decaySpeed = 0.0005 + (Math.random() * 0.0005);

    gd.obstacles.push({
        angle: spawnAngle,
        orbitIndex: targetOrbit,
        active: true,
        passed: false,
        rotationOffset: Math.random() * Math.PI * 2,
        integrity: 1.0,  
        decayRate: decaySpeed,
        maxSize: gd.obstacleSize * (0.8 + Math.random() * 0.4),
        type: isHeal ? 'HEAL' : 'ENEMY'
    });
  };

  const update = useCallback(() => {
    const gd = gameData.current;

    if (gameState === 'PLAYING') {
        gd.speedMultiplier = 1 + (gd.score * 0.005);
        
        const requiredOrbits = 2 + Math.floor(gd.score / 15);
        if (requiredOrbits > gd.activeOrbitCount && gd.activeOrbitCount < gd.maxOrbits) {
            gd.activeOrbitCount++;
            playSound('levelup');
            showNotificationMsg("YENİ ROTA EKLENDİ!"); 
            setLevel(gd.activeOrbitCount - 1);
        }

        const currentSpeed = gd.baseSpeed * gd.speedMultiplier;
        gd.player.angle += currentSpeed;
        
        const destR = gd.orbitRadii[gd.player.orbitIndex] || 100;
        gd.player.currentRadius += (destR - gd.player.currentRadius) * 0.2;

        gd.player.trail.push({
            x: gd.centerX + Math.cos(gd.player.angle) * gd.player.currentRadius,
            y: gd.centerY + Math.sin(gd.player.angle) * gd.player.currentRadius,
            life: 1.0,
            sizeRatio: 1.0 
        });
        
        for(let i = gd.player.trail.length - 1; i >= 0; i--) {
            gd.player.trail[i].life -= 0.05; 
            gd.player.trail[i].sizeRatio -= 0.03; 
            if(gd.player.trail[i].life <= 0) gd.player.trail.splice(i, 1);
        }

        let spawnRate = Math.max(35, 160 - (gd.score * 3));
        if (gd.frames % Math.floor(spawnRate) === 0) spawnObstacle();

        for (let i = gd.obstacles.length - 1; i >= 0; i--) {
            let obs = gd.obstacles[i];
            
            if (!obs.passed) {
                obs.integrity -= obs.decayRate; 
            } else {
                obs.integrity -= 0.05;
            }

            if (obs.integrity <= 0) {
                gd.obstacles.splice(i, 1);
                continue;
            }

            const r = gd.orbitRadii[obs.orbitIndex];
            const obsX = gd.centerX + Math.cos(obs.angle) * r;
            const obsY = gd.centerY + Math.sin(obs.angle) * r;
            
            const playerX = gd.centerX + Math.cos(gd.player.angle) * gd.player.currentRadius;
            const playerY = gd.centerY + Math.sin(gd.player.angle) * gd.player.currentRadius;

            const dist = Math.hypot(playerX - obsX, playerY - obsY);
            const radialDiff = Math.abs(gd.player.currentRadius - r);
            const isSolid = obs.integrity > 0.3;

            if (isSolid && radialDiff < 15 && dist < (obs.maxSize + 5)) { 
                if (obs.type === 'HEAL') {
                    gd.player.health += 25; 
                    if(gd.player.health > gd.player.maxHealth) gd.player.health = gd.player.maxHealth;
                    setHealth(gd.player.health);
                    playSound('heal');
                    createParticle(playerX, playerY, '#4ade80', 3);
                    gd.obstacles.splice(i, 1);
                    continue;
                } else {
                    const damage = Math.floor(obs.integrity * 40);
                    gd.player.health -= damage;
                    setHealth(gd.player.health);
                    triggerDamageEffect();
                    playSound('hit');
                    createParticle(playerX, playerY, '#ef4444', 3);
                    
                    gd.obstacles.splice(i, 1); 
                    
                    if (gd.player.health <= 0) {
                         setGameState('GAMEOVER');
                         playSound('die');
                         const currentScore = gd.score;
                         const savedHigh = Math.max(currentScore, parseInt(localStorage.getItem('cosmicRouteHighScore') || 0));
                         localStorage.setItem('cosmicRouteHighScore', savedHigh);
                         setHighScore(savedHigh);
                         
                         for(let k=0; k<50; k++) {
                             createParticle(
                                 gd.centerX + Math.cos(gd.player.angle) * gd.player.currentRadius,
                                 gd.centerY + Math.sin(gd.player.angle) * gd.player.currentRadius,
                                 gd.player.color, 8
                             );
                         }
                    }
                    continue;
                }
            }

            if (!obs.passed && gd.player.angle > obs.angle + 0.2) {
                obs.passed = true;
                gd.score++;
                setScore(gd.score);
                playSound('score');
                createParticle(playerX, playerY, '#fcd34d', 2);
            }
        }
        gd.frames++;
    }

    for (let i = gd.particles.length - 1; i >= 0; i--) {
        let p = gd.particles[i];
        p.x += p.vx;
        p.y += p.vy;
        p.life -= 0.03;
        if (p.life <= 0) gd.particles.splice(i, 1);
    }
    
    gd.stars.forEach(star => {
        star.y += star.speed;
        if(star.y > gd.height) star.y = 0;
    });

  }, [gameState, playSound, showNotificationMsg, triggerDamageEffect, createParticle]);

  const draw = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    const gd = gameData.current;

    ctx.fillStyle = 'rgba(10, 10, 15, 0.3)';
    ctx.fillRect(0, 0, gd.width, gd.height);

    ctx.fillStyle = '#fff';
    gd.stars.forEach(star => {
        ctx.globalAlpha = Math.random() * 0.5 + 0.3;
        ctx.beginPath();
        ctx.arc(star.x, star.y, star.size, 0, Math.PI * 2);
        ctx.fill();
    });
    ctx.globalAlpha = 1;

    for(let i = 0; i < gd.activeOrbitCount; i++) {
        const r = gd.orbitRadii[i];
        if(!r) continue;
        ctx.lineWidth = 1; 
        const isPlayerOrbit = (i === gd.player.orbitIndex && gameState === 'PLAYING');
        ctx.strokeStyle = isPlayerOrbit ? 'rgba(129, 140, 248, 0.6)' : 'rgba(129, 140, 248, 0.1)';
        ctx.beginPath();
        ctx.arc(gd.centerX, gd.centerY, r, 0, Math.PI * 2);
        ctx.stroke();
    }

    ctx.shadowBlur = 30;
    ctx.shadowColor = '#6366f1';
    ctx.fillStyle = '#4f46e5';
    ctx.beginPath();
    ctx.arc(gd.centerX, gd.centerY, 15 + Math.sin(gd.frames * 0.1) * 3, 0, Math.PI * 2);
    ctx.fill();

    if (gameState === 'PLAYING') {
        ctx.globalCompositeOperation = 'lighter';
        gd.player.trail.forEach((t) => {
            const trailSize = 6 * t.sizeRatio; 
            if(trailSize < 0) return;
            ctx.beginPath();
            ctx.arc(t.x, t.y, trailSize, 0, Math.PI * 2);
            ctx.fillStyle = `rgba(100, 150, 255, ${t.life * 0.6})`;
            ctx.fill();
        });
        ctx.shadowBlur = 15; 
        ctx.shadowColor = '#818cf8';
        ctx.fillStyle = '#fff'; 
        ctx.beginPath();
        const px = gd.centerX + Math.cos(gd.player.angle) * gd.player.currentRadius;
        const py = gd.centerY + Math.sin(gd.player.angle) * gd.player.currentRadius;
        ctx.arc(px, py, 6, 0, Math.PI * 2); 
        ctx.fill();
        ctx.globalCompositeOperation = 'source-over';
    }

    if (gameState === 'PLAYING' || gameState === 'GAMEOVER') {
        gd.obstacles.forEach(obs => {
            const r = gd.orbitRadii[obs.orbitIndex];
            if(!r) return; 

            const x = gd.centerX + Math.cos(obs.angle) * r;
            const y = gd.centerY + Math.sin(obs.angle) * r;
            
            let mainColor, coreColor, shadowColor, shadowBlur;
            
            if (obs.integrity < 0.3) {
                shadowBlur = 0;
                shadowColor = 'transparent';
                const alpha = obs.integrity * 2; 
                mainColor = `rgba(100, 100, 100, ${alpha})`; 
                coreColor = `rgba(200, 200, 200, ${alpha})`;
            } else {
                if (obs.type === 'HEAL') {
                    const vibrancy = obs.integrity; 
                    shadowBlur = 15 * vibrancy;
                    shadowColor = `rgba(74, 222, 128, ${vibrancy})`; 
                    mainColor = `rgba(34, 197, 94, ${vibrancy})`;   
                    coreColor = '#14532d';
                } else {
                    const vibrancy = obs.integrity; 
                    shadowBlur = 15 * vibrancy;
                    shadowColor = `rgba(244, 63, 94, ${vibrancy})`; 
                    mainColor = `rgba(225, 29, 72, ${vibrancy})`;   
                    coreColor = '#881337';
                }
            }

            ctx.shadowBlur = shadowBlur;
            ctx.shadowColor = shadowColor;
            ctx.fillStyle = mainColor;
            
            ctx.save();
            ctx.translate(x, y);
            ctx.rotate(obs.rotationOffset + (gd.frames * 0.05)); 
            
            const pulse = 1 + Math.sin(gd.frames * 0.2) * 0.1; 
            const currentSize = obs.maxSize * obs.integrity * pulse;

            if (currentSize > 0) {
                if (obs.type === 'HEAL' && obs.integrity > 0.3) {
                        ctx.beginPath();
                        ctx.arc(0, 0, currentSize/1.5, 0, Math.PI*2);
                        ctx.fill();
                        ctx.fillStyle = '#fff';
                        ctx.fillRect(-currentSize/6, -currentSize/2, currentSize/3, currentSize);
                        ctx.fillRect(-currentSize/2, -currentSize/6, currentSize, currentSize/3);
                } else {
                    ctx.rotate(Math.PI / 4); 
                    if (obs.integrity < 0.3) {
                        ctx.strokeStyle = mainColor;
                        ctx.lineWidth = 2;
                        ctx.strokeRect(-currentSize/2, -currentSize/2, currentSize, currentSize);
                    } else {
                        ctx.fillRect(-currentSize/2, -currentSize/2, currentSize, currentSize);
                        ctx.fillStyle = coreColor;
                        ctx.fillRect(-currentSize/4, -currentSize/4, currentSize/2, currentSize/2);
                    }
                }
            }
            ctx.restore();
        });
    }

    gd.particles.forEach(p => {
        ctx.shadowBlur = 10;
        ctx.shadowColor = p.color;
        ctx.fillStyle = p.color;
        ctx.globalAlpha = p.life;
        ctx.beginPath();
        ctx.fillRect(p.x, p.y, 5, 5);
        ctx.globalAlpha = 1;
    });

  }, [gameState]);

  const loop = useCallback(() => {
    update();
    draw();
    requestRef.current = requestAnimationFrame(loop);
  }, [update, draw]);

  const startGame = () => {
    initAudio();
    const gd = gameData.current;
    
    setScore(0);
    setHealth(100);
    setLevel(1);
    setNotification('');
    
    gd.score = 0;
    gd.frames = 0;
    gd.speedMultiplier = 1;
    gd.activeOrbitCount = 2;
    gd.obstacles = [];
    gd.particles = [];
    gd.player.angle = 0;
    gd.player.orbitIndex = 0;
    gd.player.trail = [];
    gd.player.health = 100;
    
    calculateOrbitRadii();
    gd.player.currentRadius = gd.orbitRadii[0];
    gd.player.targetRadius = gd.orbitRadii[0];

    setGameState('PLAYING');
  };

  const handleGesture = useCallback((delta) => {
    const gd = gameData.current;
    const threshold = 30;

    if (delta < -threshold) { 
        if (gd.player.orbitIndex < gd.activeOrbitCount - 1) {
            gd.player.orbitIndex++;
            gd.player.targetRadius = gd.orbitRadii[gd.player.orbitIndex];
            playSound('move');
            spawnJumpEffect();
        }
    } else if (delta > threshold) { 
        if (gd.player.orbitIndex > 0) {
            gd.player.orbitIndex--;
            gd.player.targetRadius = gd.orbitRadii[gd.player.orbitIndex];
            playSound('move');
            spawnJumpEffect();
        }
    }
  }, [playSound , spawnJumpEffect]);

  // --- Olay Dinleyicileri ---
  useEffect(() => {
    requestRef.current = requestAnimationFrame(loop);
    
    const handleResize = () => {
        const canvas = canvasRef.current;
        if (canvas) {
            const width = window.innerWidth;
            const height = window.innerHeight;
            canvas.width = width;
            canvas.height = height;
            
            gameData.current.width = width;
            gameData.current.height = height;
            gameData.current.centerX = width / 2;
            gameData.current.centerY = height / 2;
            
            calculateOrbitRadii();
            
            if (gameState !== 'PLAYING') {
                 gameData.current.stars = [];
                 for(let i=0; i<80; i++) { 
                    gameData.current.stars.push({
                        x: Math.random() * width,
                        y: Math.random() * height,
                        size: Math.random() * 1.5,
                        speed: Math.random() * 0.3 + 0.05
                    });
                }
            }
        }
    };

    window.addEventListener('resize', handleResize);
    handleResize(); 
    
    const saved = localStorage.getItem('cosmicRouteHighScore');
    if(saved) setHighScore(parseInt(saved));

    return () => {
        cancelAnimationFrame(requestRef.current);
        window.removeEventListener('resize', handleResize);
    };
  }, [loop, gameState, calculateOrbitRadii]);

  useEffect(() => {
    let startX, startY;
    
    const handleTouchStart = (e) => {
        if(e.target.tagName === 'BUTTON') return;
        startX = e.touches[0].clientX;
        startY = e.touches[0].clientY;
    };
    
    const handleTouchEnd = (e) => {
        if(e.target.tagName === 'BUTTON' || gameState !== 'PLAYING') return;
        const endX = e.changedTouches[0].clientX;
        const endY = e.changedTouches[0].clientY;
        const distStart = Math.hypot(startX - gameData.current.centerX, startY - gameData.current.centerY);
        const distEnd = Math.hypot(endX - gameData.current.centerX, endY - gameData.current.centerY);
        handleGesture(distEnd - distStart);
    };

    const handleMouseDown = (e) => {
        if(e.target.tagName === 'BUTTON') return;
        startX = e.clientX;
        startY = e.clientY;
    };

    const handleMouseUp = (e) => {
         if(e.target.tagName === 'BUTTON' || gameState !== 'PLAYING') return;
         const distStart = Math.hypot(startX - gameData.current.centerX, startY - gameData.current.centerY);
         const distEnd = Math.hypot(e.clientX - gameData.current.centerX, e.clientY - gameData.current.centerY);
         handleGesture(distEnd - distStart);
    };
    
    const handleKeyDown = (e) => {
        if(gameState !== 'PLAYING') return;
        if(e.key === 'ArrowDown' || e.key === 's') handleGesture(-50); 
        if(e.key === 'ArrowUp' || e.key === 'w') handleGesture(50); 
    };

    window.addEventListener('touchstart', handleTouchStart, {passive: false});
    window.addEventListener('touchend', handleTouchEnd, {passive: false});
    window.addEventListener('mousedown', handleMouseDown);
    window.addEventListener('mouseup', handleMouseUp);
    window.addEventListener('keydown', handleKeyDown);
    
    return () => {
        window.removeEventListener('touchstart', handleTouchStart);
        window.removeEventListener('touchend', handleTouchEnd);
        window.removeEventListener('mousedown', handleMouseDown);
        window.removeEventListener('mouseup', handleMouseUp);
        window.removeEventListener('keydown', handleKeyDown);
    };
  }, [gameState, handleGesture]);

  return (
    <div className="container">
      {/* Pure CSS Styles */}
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Orbitron:wght@400;700;900&display=swap');

        body { margin: 0; overflow: hidden; background-color: #050505; font-family: 'Orbitron', sans-serif; user-select: none; -webkit-user-select: none; }
        .container { position: relative; width: 100%; height: 100vh; overflow: hidden; touch-action: none; }
        .canvas-elem { display: block; width: 100%; height: 100%; }
        .overlay { position: absolute; top: 0; left: 0; width: 100%; height: 100%; pointer-events: none; z-index: 10; transition: opacity 0.1s; }
        
        /* UI Layers */
        .ui-layer { position: absolute; top: 0; left: 0; right: 0; bottom: 0; display: flex; flex-direction: column; justify-content: center; align-items: center; z-index: 20; color: white; text-align: center; padding: 20px; }
        .menu-bg { background-color: rgba(0, 0, 0, 0.4); z-index: 40; }
        .game-over-bg { background-color: rgba(0, 0, 0, 0.8); z-index: 50; }

        /* Typography */
        .title { font-size: 3rem; font-weight: 900; margin-bottom: 1.5rem; letter-spacing: 0.1em; color: #818cf8; text-shadow: 0 0 30px #4f46e5; }
        .text-score { font-size: 3rem; font-weight: 900; color: transparent; background: linear-gradient(to bottom, white, #818cf8); -webkit-background-clip: text; background-clip: text; filter: drop-shadow(0 0 10px rgba(99,102,241,0.8)); margin: 0; }
        .text-level { font-size: 0.875rem; color: #818cf8; letter-spacing: 0.2em; opacity: 0.8; margin-top: 0.25rem; }
        .text-notification { font-size: 1.5rem; font-weight: bold; color: #fbbf24; text-shadow: 0 0 20px rgba(245,158,11,1); opacity: 0; transition: opacity 0.5s; }
        .text-notification.visible { opacity: 1; }
        .text-gameover-title { font-size: 3rem; font-weight: 900; color: #ef4444; margin-bottom: 0.5rem; text-shadow: 0 0 30px red; letter-spacing: -0.05em; }
        .text-gameover-sub { font-size: 1.5rem; margin-bottom: 1.5rem; color: #fca5a5; font-weight: bold; }
        .text-result { font-size: 2rem; margin-bottom: 0.5rem; color: white; }
        .text-highlight { color: #818cf8; }
        .text-record { font-size: 1.25rem; color: #9ca3af; margin-bottom: 2.5rem; letter-spacing: 0.1em; }

        /* Buttons */
        .btn { background: #4f46e5; color: white; padding: 15px 40px; font-family: 'Orbitron', sans-serif; font-weight: 900; font-size: 1.25rem; text-transform: uppercase; letter-spacing: 0.1em; border: none; cursor: pointer; box-shadow: 6px 0px 0px #4338ca; transition: transform 0.1s; clip-path: polygon(10% 0, 100% 0%, 100% 100%, 0 100%, 0% 20%); display: flex; align-items: center; gap: 10px; pointer-events: auto; }
        .btn:active { transform: translateX(4px); box-shadow: 2px 0px 0px #4338ca; }
        .btn-restart { background: #db2777; box-shadow: 6px 0px 0px #be185d; }
        .btn-restart:active { box-shadow: 2px 0px 0px #be185d; }

        /* HUD Elements */
        .hud-top { position: absolute; top: 1.25rem; left: 50%; transform: translateX(-50%); display: flex; align-items: center; justify-content: center; z-index: 30; }
        .heart-icon { width: 1.5rem; height: 1.5rem; color: #ef4444; margin-right: 0.5rem; position: absolute; left: -2rem; animation: pulse 1.5s infinite; }
        .health-container { width: 200px; height: 1.5rem; background: rgba(15, 23, 42, 0.8); border: 2px solid #4f46e5; border-radius: 9999px; overflow: hidden; box-shadow: 0 0 15px rgba(79, 70, 229, 0.5); padding: 2px; }
        .health-bar { height: 100%; background: linear-gradient(90deg, #ef4444, #22c55e); background-size: 200% 100%; border-radius: 9999px; transition: width 0.2s cubic-bezier(0.4, 0, 0.2, 1), background-position 0.2s; }
        
        .score-box { position: absolute; top: 3.5rem; left: 0; width: 100%; text-align: center; pointer-events: none; z-index: 20; }
        .notification-box { position: absolute; top: 33%; width: 100%; text-align: center; pointer-events: none; z-index: 30; }

        /* Tutorial Box */
        .tutorial-box { background: rgba(15, 23, 42, 0.9); border: 1px solid #6366f1; padding: 1.25rem; border-radius: 0.5rem; width: 100%; max-width: 28rem; margin-bottom: 2rem; text-align: left; box-shadow: 0 0 30px rgba(99, 102, 241, 0.2); color: #cbd5e1; font-size: 0.9rem; }
        .tutorial-header { font-size: 1.25rem; text-align: center; color: #a5b4fc; margin-bottom: 1rem; border-bottom: 1px solid #312e81; padding-bottom: 0.5rem; font-weight: bold; }
        .tutorial-row { display: flex; align-items: center; margin-bottom: 0.75rem; }
        .emoji { margin-right: 0.75rem; font-size: 1.25rem; }
        .highlight { color: white; font-weight: bold; text-shadow: 0 0 5px rgba(255,255,255,0.5); }
        .divider { height: 1px; background: #312e81; margin: 0.5rem 0; width: 100%; }
        .text-green { color: #4ade80; font-weight: bold; }
        .text-red { color: #ef4444; font-weight: bold; }
        .text-gray { color: #9ca3af; font-weight: bold; }

        /* Swipe Hint */
        .swipe-hint { margin-top: 1.5rem; display: flex; flex-direction: column; align-items: center; gap: 0.5rem; color: #a5b4fc; opacity: 0.8; font-size: 0.875rem; }
        .swipe-circle { width: 1.5rem; height: 1.5rem; border: 2px solid #a5b4fc; border-radius: 50%; position: relative; }
        .swipe-circle::after { content: ''; position: absolute; top: 50%; left: 50%; width: 4px; height: 4px; background: #fff; border-radius: 50%; transform: translate(-50%, -50%); animation: swipe-anim 1.5s infinite; }

        /* Animations */
        @keyframes pulse { 0%, 100% { transform: scale(1); } 50% { transform: scale(1.2); } }
        @keyframes swipe-anim { 0% { transform: translate(-50%, -50%); opacity: 0; } 20% { opacity: 1; } 80% { transform: translate(-50%, 15px); opacity: 0; } 100% { opacity: 0; } }
        
        /* Media Queries */
        @media (min-width: 768px) {
            .title { font-size: 4.5rem; }
            .health-container { width: 300px; }
            .text-gameover-title { font-size: 4.5rem; }
        }
      `}</style>

      {/* Canvas */}
      <canvas ref={canvasRef} className="canvas-elem" />
      
      {/* Damage Overlay */}
      <div className="overlay" style={{ 
          background: 'radial-gradient(circle, transparent 50%, rgba(255, 0, 0, 0.5) 100%)',
          opacity: showDamageOverlay ? 1 : 0 
      }} />

      {/* HUD */}
      {gameState === 'PLAYING' && (
        <>
            <div className="hud-top">
                <Heart className="heart-icon" fill="currentColor" />
                <div className="health-container">
                    <div className="health-bar" style={{ width: `${health}%`, backgroundPosition: `${health}% 0` }} />
                </div>
            </div>

            <div className="score-box">
                <div className="text-score">{score}</div>
                <div className="text-level">SEVİYE {level}</div>
            </div>

            <div className="notification-box">
                <div className={`text-notification ${notification ? 'visible' : ''}`}>
                    {notification}
                </div>
            </div>
        </>
      )}

      {/* Ana Menü */}
      {gameState === 'MENU' && (
        <div className="ui-layer menu-bg">
            <h1 className="title">KOZMİK<br/>ROTA</h1>
            
            <div className="tutorial-box">
                <h3 className="tutorial-header">KONTROL SİSTEMİ</h3>
                <div className="tutorial-row">
                    <span className="emoji">👆</span> 
                    <span>Merkeze doğru kaydır: <span className="highlight">İÇERİ GİT</span></span>
                </div>
                <div className="tutorial-row">
                    <span className="emoji">👇</span> 
                    <span>Dışarı doğru kaydır: <span className="highlight">DIŞARI ÇIK</span></span>
                </div>
                <div className="divider"></div>
                <div><span className="text-green">YEŞİL</span> al, <span className="text-red">KIRMIZI</span>dan kaç.</div>
                <div style={{marginTop: '5px'}}>Silik engeller <span className="text-gray">ZARARSIZDIR</span>.</div>
            </div>

            <button onClick={startGame} className="btn">
                <Play size={24} /> ROTA OLUŞTUR
            </button>
            
            <div className="swipe-hint">
                 <div className="swipe-circle"></div>
                 <span>Kontrol için kaydır</span>
            </div>
        </div>
      )}

      {/* Oyun Bitti */}
      {gameState === 'GAMEOVER' && (
        <div className="ui-layer game-over-bg">
             <h2 className="text-gameover-title">SİNYAL KAYBI</h2>
             <div className="text-gameover-sub">HAYATTA KALMA: BAŞARISIZ</div>
             
             <div className="text-result">SKOR: <span className="text-highlight">{score}</span></div>
             <div className="text-record">REKOR: <span className="highlight">{highScore}</span></div>
             
             <button onClick={startGame} className="btn btn-restart">
                <RotateCcw size={24} /> YENİDEN BAĞLAN
            </button>
        </div>
      )}
    </div>
  );
}